import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { addressString } from '@core/util/address';
import { Address } from '@models/address';
import { Broker } from '@models/brokerage';
import { Employee } from '@models/company';
import { Brokerage, MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
	CreateBrokerageComponent,
	CreateOrUpdateBrokerageModel,
} from './create/create.component';
import { TestHooks } from './brokerage.hooks';
import { AI_GENERATED_ICON } from '@core/util/icon';
import { getBrokerageType } from '@state/utils/helper';
import { MatterDocumentType } from '@models/matter-file';
import { DocumentProcessorService } from '@services/document-ai/document-processor.service';

@UntilDestroy()
@Component({
	selector: 'app-brokerage',
	templateUrl: './brokerage.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class BrokerageComponent implements OnInit {
	testHooks = TestHooks;
	@Output() onFocus = new EventEmitter<string>();

	@Input() label: string;
	@Input() set isClientBrokerage(isClient: boolean) {
		this.isClient = isClient;
	}
	@Input() orientation: 'row' | 'col' = 'col';
	@Input() showBrokerageNameAndLabel = true;

	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.clientBrokerage)
	clientBrokerage$: Observable<Brokerage>;
	@Select(SelectedMatter.otherPartyBrokerage)
	otherPartyBrokerage$: Observable<Brokerage>;
	@Select(SelectedMatter.matterFileType)
	matterFileType$: Observable<MatterFileType>;
	public isClient$ = new BehaviorSubject<boolean>(false);

	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	public hoverShadow = true;
	public matterFileType: MatterFileType;
	public brokerage$!: Observable<Brokerage>;
	public sellerBuyerBrokerLabel$!: Observable<'Buyer' | 'Seller'>;
	public brokerageFieldMappings$ = this.isClient$.pipe(
		switchMap((isClient) =>
			isClient
				? this.store.select(SelectedMatter.clientBrokerageFieldMappings)
				: this.store.select(SelectedMatter.otherPartyBrokerageFieldMappings),
		),
	);

	public isLoading$: Observable<boolean> = EMPTY;

	public set isClient(isClient: boolean) {
		this.isClient$.next(isClient);
	}

	public get isClient(): boolean {
		return !!this.isClient$.getValue();
	}

	constructor(
		private matterDispatch: MatterDispatch,
		private dialog: MatDialog,
		private store: Store,
		private documentProcessorService: DocumentProcessorService,
	) {}

	ngOnInit(): void {
		this.brokerage$ = this.isClient$.pipe(
			switchMap((isClient) =>
				isClient ? this.clientBrokerage$ : this.otherPartyBrokerage$,
			),
		);

		this.isLoading$ = combineLatest([
			this.matterFileType$,
			this.isClient$,
		]).pipe(
			map(([fileType, isClient]) => {
				return (fileType === MatterFileType.PURCHASE) === isClient
					? MatterDocumentType.BUYER_COMMISSION_STATEMENT
					: MatterDocumentType.SELLER_COMMISSION_STATEMENT;
			}),
			switchMap((documentType) =>
				this.documentProcessorService.isLoading$(documentType),
			),
		);

		this.sellerBuyerBrokerLabel$ = combineLatest([
			this.matterFileType$,
			this.isClient$,
		]).pipe(
			map(([fileType, isClient]) => {
				if (fileType === MatterFileType.PURCHASE) {
					return isClient ? 'Buyer' : 'Seller';
				}
				return isClient ? 'Seller' : 'Buyer';
			}),
		);

		this.matterFileType$
			.pipe(untilDestroyed(this))
			.subscribe((matterFileType) => {
				this.matterFileType = matterFileType;
			});
	}

	setAddress(address: Address): string {
		return addressString(address);
	}

	_onFocus(): void {
		this.onFocus.emit('listingBroker');
	}

	removeBrokerage(): void {
		this.matterFileType$.pipe(take(1)).subscribe((matterFileType) => {
			this.matterDispatch.RemoveBrokerage(
				this.matterId,
				this.isClient,
				matterFileType,
			);
			this._onFocus();
		});
	}

	selectBrokerage(broker: Brokerage): void {
		this.matterFileType$.pipe(take(1)).subscribe((matterFileType) => {
			this.matterDispatch.SelectBrokerage(
				this.matterId,
				broker.id,
				this.isClient,
				matterFileType,
				broker.leap?.cardId,
			);
		});
	}

	updateBrokerage(): void {
		this.brokerage$
			.pipe(
				take(1),
				untilDestroyed(this),
				map(
					(brokerage) =>
						({
							brokerage,
							isClient: this.isClient,
							name: brokerage.name,
						}) as CreateOrUpdateBrokerageModel,
				),
			)
			.subscribe((data) => {
				this.dialog.open(CreateBrokerageComponent, {
					data,
					height: '700px',
					width: '800px',
				});
			});
	}

	selectBroker(broker: Broker): void {
		this.matterFileType$.pipe(take(1)).subscribe((matterFileType) => {
			this.matterDispatch.SelectBroker(
				this.matterId,
				broker.brokerageId,
				broker.id,
				this.isClient,
				matterFileType,
				broker.leap?.personId,
			);
		});
	}

	createBroker(employee: Employee): void {
		combineLatest([this.brokerage$, this.matterFileType$])
			.pipe(take(1))
			.subscribe(([brokerage, matterFileType]) => {
				this.matterDispatch.CreateBroker(
					this.matterId,
					employee,
					this.isClient,
					brokerage.id,
					matterFileType,
				);
			});
	}

	removeBroker(): void {
		this.matterFileType$.pipe(take(1)).subscribe((matterFileType) => {
			this.matterDispatch.RemoveBroker(
				this.matterId,
				this.isClient,
				matterFileType,
			);
		});
	}

	brokerageDropdownOptionSelect(event: {
		result: { name?: 'Edit' | 'Remove' };
	}): void {
		switch (event.result.name) {
			case 'Edit':
				this.updateBrokerage();
				break;
			case 'Remove':
				this.removeBrokerage();
				break;
		}
	}

	fileNumberChange = (fileNumber: string) => {
		const brokerageKey = getBrokerageType(this.isClient, this.matterFileType);
		if (brokerageKey) {
			this.matterDispatch.OverwriteFields(
				`${brokerageKey}.fileNumber`,
				fileNumber,
			);
		}
	};
}
